import { CloseOutlined, FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { PureButton } from '../../../../../common/ui/pure-button/pure-button'
import { ObjectItem } from '../../../../../entities/objects/types/object.types'
import { MilestoneChecklistStatus } from '../../../../../entities/workflow/constants/workflow.constants'
import { useChecklistResponsible } from '../../../../../entities/workflow/hooks/checklist.hooks'
import {
  useGetChecklist,
  useGetChecklistAvailability,
  useSaveChecklist,
  useStartChecklist,
  useUpdateChecklist,
} from '../../../../../entities/workflow/hooks/workflow.hooks'
import { MilestoneItem } from '../../../../../entities/workflow/types/workflow.types'
import { SearchUser } from '../../../../shared/components/search-user/search-user'
import { FormBuilderFormFactory } from '../../../../shared/form-builder/form-builder-form-factory'
import { disableFillStatuses, unblockedFormStatuses } from '../../constants/workspace.constants'
import { getMilestoneStatusColor } from '../../helpers/get-milestone-status-color'
import { MilestoneStatusButton } from '../milestone-status-button'
import tableStyles from '../workspace-table/workspace-table.module.scss'

type MilestoneFormProps = {
  milestone: MilestoneItem
  object: ObjectItem
  handleSaveChecklist: (status: MilestoneChecklistStatus) => void
  isExpanded: boolean
  onClose?: () => void
  onExpand?: () => void
}

type FormResultType = {
  errors: Promise<Record<string, any>> | null
  values: Record<string, any>
}

export const MilestoneForm = forwardRef(
  ({ milestone, object, handleSaveChecklist, isExpanded, onClose, onExpand }: MilestoneFormProps, ref) => {
    const { t } = useTranslation()
    const checklist = useGetChecklist(object.id, milestone.id)
    const { data: availability, isLoading: isAvailabilityLoading } = useGetChecklistAvailability(object.id, milestone.id)
    const formBuilderRef = useRef<{ submit: () => FormResultType; clear: () => void } | null>(null)
    const { mutateAsync: saveChecklist } = useSaveChecklist()
    const { mutateAsync: updateChecklist } = useUpdateChecklist()
    const { mutateAsync: startChecklist } = useStartChecklist()
    const { setResponsible } = useChecklistResponsible()

    const canSetResponsible = true

    const submitChecklist = async (status: MilestoneChecklistStatus) => {
      let updatedStatus = status
      if (status === MilestoneChecklistStatus.Completed && milestone.shouldBeChecked) {
        updatedStatus = MilestoneChecklistStatus.ReadyToCheck
      }

      let formResult: FormResultType | undefined = {
        errors: new Promise((res) => res({})),
        values: checklist.data?.checklist ?? {},
      }

      if (updatedStatus === MilestoneChecklistStatus.Completed || updatedStatus === MilestoneChecklistStatus.ReadyToCheck) {
        formResult = formBuilderRef.current?.submit()
        const errors = (await formResult?.errors) ?? {}
        const hasErrors = Object.keys(errors).length || !formResult?.values

        if (hasErrors) {
          return false
        }
      }

      try {
        if (checklist.data?.id) {
          await updateChecklist({
            ...checklist.data,
            objectId: object.id,
            status: updatedStatus,
            milestoneId: milestone.id,
            checklist: formResult?.values ?? {},
          })
        } else {
          await saveChecklist({
            objectId: object.id,
            status: updatedStatus,
            milestoneId: milestone.id,
            checklist: formResult?.values ?? {},
            comment: {},
          })
        }
      } catch (e) {
        return false
      }

      formBuilderRef.current?.clear()

      return true
    }

    useImperativeHandle(ref, () => ({
      submit: submitChecklist,
    }))

    const startMilestone = () => {
      startChecklist({
        objectId: object.id,
        milestoneId: milestone.id,
      })
    }

    const handleSetResponsible = async (email: string) => {
      await setResponsible(object.id, milestone.id, email, checklist.data)
    }

    if (!milestone) {
      return <div>{t('workflow.noMilestone')}</div>
    }

    if (milestone && checklist.isLoading) {
      return <div>{t('common.loading')}</div>
    }

    return (
      <>
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <span
              className={tableStyles.statusIndicator}
              style={{ background: getMilestoneStatusColor(checklist.data?.status) }}
            ></span>
            {t(`status.${checklist.data?.status ?? MilestoneChecklistStatus.New}`)}
          </div>
          {!isAvailabilityLoading && availability?.canBeStarted && availability.configurationExists ? (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {!checklist.data?.status || !disableFillStatuses.includes(checklist.data?.status) ? (
                <div className="d-flex align-items-center">
                  <b>{t('common.actions')}:</b>
                  <MilestoneStatusButton
                    isStarted={!!checklist.data?.factStart}
                    start={startMilestone}
                    status={checklist.data?.status ?? MilestoneChecklistStatus.New}
                    statusChange={handleSaveChecklist}
                  />
                </div>
              ) : null}
            </>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {isAvailabilityLoading ? <div>Управление статусами загружается</div> : null}
              {!isAvailabilityLoading && !availability?.canBeStarted ? <div>Чеклист не может быть начат</div> : null}
              {!isAvailabilityLoading && !availability?.configurationExists ? (
                <div>Чеклист не может быть начат - конфигурация отсутсвует</div>
              ) : null}
            </>
          )}
          <div>
            <PureButton
              onClick={() => onExpand?.()}
              type="text"
            >
              {isExpanded ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
            </PureButton>
            <PureButton
              className="ml-1"
              onClick={() => onClose?.()}
              type="text"
            >
              <CloseOutlined />
            </PureButton>
          </div>
        </div>
        {canSetResponsible && (
          <SearchUser
            isInvited={checklist.data?.isResponsibleInvited}
            label={t('workspace.setResponsible')}
            onChange={handleSetResponsible}
            value={checklist?.data?.responsibleId}
          />
        )}
        <div className={checklist.data?.factStart && !canSetResponsible ? '' : 'mt-3'}>
          <FormBuilderFormFactory
            ref={formBuilderRef}
            comments={checklist.data?.comment ?? []}
            config={milestone?.checkList}
            fieldsDisabled={!checklist.data?.status || !unblockedFormStatuses.includes(checklist.data?.status)}
            values={checklist.data?.checklist}
          />
        </div>
      </>
    )
  },
)
